import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import { makePrioStyles } from '../../../../../../theme/utils';
import { PrioTheme } from '../../../../../../theme/types';
import {
  InformationBox,
  Input,
  Modal,
  Select,
  Toggle,
} from '@prio365/prio365-react-library';
import { notification } from 'antd';
import { User } from '@sentry/react';
import { useSelector } from 'react-redux';
import {
  RootReducerState,
  getUserMe,
} from '../../../../../../apps/main/rootReducer';
import Flex from '../../../../../../components/Flex';
import { ProjectExtension } from '../../../../../../models/ProjectExtension';
import { ProjectId } from '../../../../../../models/Types';
import {
  apiProjectExtensionTrigger,
  apiUpdateProjectFolderStructure,
} from '../../../../api';
import useProjectsContext from '../../../../hooks/useProjectsContext';
import {
  SharepointProjectExtensionFormModel,
  SharepointProjectExtensionFormModelType,
} from './SharepointProjectExtensionForm';
import { ProjectFileSystemStructure } from '../../../../../../models/ProjectFileStructure';

const useStyles = makePrioStyles((theme) => ({
  root: {},
}));

interface SharepointProjectExtensionModalProps {
  projectId: ProjectId;
  projectExtension: ProjectExtension;
  closeModal: () => void;
  reloadProjectExtension: () => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  setNewProjectFolderStructureId: (value: string) => void;
  projectFileStructure: ProjectFileSystemStructure[];
  toggleValue: boolean;
  setToggleValue: (value: boolean) => void;
  modalType: SharepointProjectExtensionFormModelType;
  newProjectFolderStructureId: string;
  initialValue: SharepointProjectExtensionFormModel;
}

export const SharepointProjectExtensionModal: React.FC<
  SharepointProjectExtensionModalProps
> = (props) => {
  //#region ------------------------------ Defaults
  const {
    modalType,
    projectId,
    projectExtension,
    inputValue,
    setInputValue,
    newProjectFolderStructureId,
    setNewProjectFolderStructureId,
    toggleValue,
    setToggleValue,
    projectFileStructure,
    closeModal,
    reloadProjectExtension,
    initialValue,
  } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { getProjectById } = useProjectsContext();

  const projectNumber = getProjectById(projectId)?.number;
  const userMe = useSelector<RootReducerState, User>(getUserMe);
  const userIsGlobalAdmin =
    userMe?.prioData.globalRoles.includes('globalAdmin') ?? false;
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleOkCreateFolderStructure = async () => {
    if (userIsGlobalAdmin) {
      const result = await apiProjectExtensionTrigger(
        projectId,
        projectExtension.projectExtensionId,
        'CreateFolderStructure'
      );
      if (result.status >= 200 && result.status < 300) {
        notification.open({
          message: t('common:success'),
          description: t(
            'projects:successMessages.createNewFolderSuccessMessage'
          ),
        });
      } else {
        notification.open({
          message: t('common:error'),
          description: t('projects:errorMessages.createNewFolderError'),
        });
      }
    }
  };

  const handleOkUpdateFolderStructure = async () => {
    if (userIsGlobalAdmin) {
      const result = await apiUpdateProjectFolderStructure(
        projectId,
        newProjectFolderStructureId,
        toggleValue
      );
      const isSuccessful = result.status >= 200 && result.status < 300;
      notification.open({
        message: t(`common:${isSuccessful ? 'success' : 'error'}`),
        description: t(
          `projects:${isSuccessful ? 'success' : 'error'}Messages.updateNewFolderStructure${isSuccessful ? 'Success' : 'Error'}`
        ),
      });
      if (isSuccessful) setTimeout(() => reloadProjectExtension(), 1000);
    }
  };

  const handleModalOk = () => {
    if (modalType === 'createFolderStructureModal') {
      handleOkCreateFolderStructure();
    }
    if (modalType === 'updateFolderStructureModal') {
      handleOkUpdateFolderStructure();
    }
    closeModal();
  };
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  //#region ------------------------------ Components
  const renderUniqueContent = () => {
    if (modalType === 'updateFolderStructureModal') {
      return (
        <>
          <InformationBox
            type="warning"
            description={t(`projects:adminSetting.${modalType}.content`)}
            marginBottom="none"
          />
          <Select
            label={t(`projects:adminSetting.${modalType}.selectLabel`)}
            defaultValue={initialValue.projectFileSystemStructureId}
            options={projectFileStructure.map((psf) => {
              return {
                label: psf.name,
                value: psf.projectFileSystemStructureId,
              };
            })}
            onChange={setNewProjectFolderStructureId}
          />
          <Flex.Column childrenGap={theme.spacing.small}>
            <Toggle
              checked={toggleValue}
              onChange={setToggleValue}
              label={t(`projects:adminSetting.${modalType}.toggleLabel`)}
            />
            <div
              style={{
                fontSize: theme.font.fontSize.small,
                color: theme.colors.application.typography.muted,
              }}
            >
              {t(`projects:adminSetting.${modalType}.toggleDescription`)}
            </div>
          </Flex.Column>
          <Input
            full
            label={t(`projects:adminSetting.${modalType}.inputLabel`, {
              projectNumber: projectNumber,
            })}
            placeholder={t(
              `projects:adminSetting.${modalType}.inputPlaceholder`
            )}
            value={inputValue}
            onChange={setInputValue}
            isValid={!inputValue || inputValue === projectNumber}
          />
          <div>{t(`projects:adminSetting.${modalType}.content2`)}</div>
        </>
      );
    }
    return null;
  };
  //#endregion

  return (
    <Modal
      className={classes.root}
      visible={modalType !== undefined}
      title={t(`projects:adminSetting.${modalType}.title`)}
      onOk={() => handleModalOk()}
      onClose={closeModal}
      okText={t(`projects:adminSetting.${modalType}.okText`)}
      cancelText={t(`projects:adminSetting.${modalType}.cancelText`)}
      width="600px"
      okButtonProps={
        modalType === 'updateFolderStructureModal'
          ? {
              disabled:
                newProjectFolderStructureId === undefined ||
                newProjectFolderStructureId ===
                  initialValue.projectFileSystemStructureId ||
                inputValue !== projectNumber,
            }
          : {}
      }
    >
      <Flex.Column childrenGap={theme.spacing.large}>
        {modalType !== 'updateFolderStructureModal'
          ? t(`projects:adminSetting.${modalType}.content`)
          : null}
        {renderUniqueContent()}
      </Flex.Column>
    </Modal>
  );
};

export default SharepointProjectExtensionModal;
